package nl.appstuff.padelchecker.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import nl.appstuff.padelchecker.site.PageSeo
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.StructuredData
import nl.appstuff.padelchecker.site.components.NavBar
import nl.appstuff.padelchecker.site.components.sections.FooterSection
import nl.appstuff.padelchecker.site.localization.LocalSiteLanguage
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import nl.appstuff.padelchecker.site.localization.SiteStrings
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Page
@Composable
fun VergelijkingPage() {
    val strings = LocalSiteStrings.current
    val language = LocalSiteLanguage.current
    PageSeo(
        title = strings.vergelijkingSeoTitle,
        description = strings.vergelijkingSeoDescription,
        canonicalPath = "/vergelijking",
        lang = language.tag,
    )
    ComparisonStructuredData(strings)

    Column(modifier = Modifier.fillMaxWidth().minHeight(100.vh)) {
        NavBar()
        VergelijkingContent(strings)
        FooterSection()
    }
}

@Composable
private fun ComparisonStructuredData(strings: SiteStrings) {
    StructuredData(
        """
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "${strings.vergelijkingStructuredFaq1Question}",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "${strings.vergelijkingStructuredFaq1Answer}"
              }
            },
            {
              "@type": "Question",
              "name": "${strings.vergelijkingStructuredFaq2Question}",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "${strings.vergelijkingStructuredFaq2Answer}"
              }
            },
            {
              "@type": "Question",
              "name": "${strings.vergelijkingStructuredFaq3Question}",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "${strings.vergelijkingStructuredFaq3Answer}"
              }
            },
            {
              "@type": "Question",
              "name": "${strings.vergelijkingStructuredFaq4Question}",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "${strings.vergelijkingStructuredFaq4Answer}"
              }
            }
          ]
        }
        """.trimIndent()
    )
}

@Composable
private fun VergelijkingContent(strings: SiteStrings) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("flex", "1") }
            .background(SiteColors.backgroundLight)
            .padding(topBottom = 64.px, leftRight = 24.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(900.px),
            verticalArrangement = Arrangement.spacedBy(28.px),
        ) {
            SpanText(
                strings.vergelijkingHeading,
                modifier = Modifier
                    .fontSize(40.px)
                    .lineHeight(1.15)
                    .fontWeight(FontWeight.Bold)
                    .color(SiteColors.textLight),
            )

            SpanText(
                strings.vergelijkingIntro,
                modifier = Modifier
                    .fontSize(17.px)
                    .lineHeight(1.6)
                    .color(SiteColors.textMutedLight),
            )

            SectionHeading(strings.vergelijkingSectionTable)

            ComparisonTable(strings)

            SectionHeading(strings.vergelijkingSectionWhyNotEnough)
            Paragraph(strings.vergelijkingWhyNotEnoughBody1)
            Paragraph(strings.vergelijkingWhyNotEnoughBody2)

            SectionHeading(strings.vergelijkingSectionUnique)
            BulletList(
                listOf(
                    strings.vergelijkingUniqueBullet1,
                    strings.vergelijkingUniqueBullet2,
                    strings.vergelijkingUniqueBullet3,
                    strings.vergelijkingUniqueBullet4,
                )
            )

            SectionHeading(strings.vergelijkingSectionNotDoes)
            BulletList(
                listOf(
                    strings.vergelijkingNotDoesBullet1,
                    strings.vergelijkingNotDoesBullet2,
                    strings.vergelijkingNotDoesBullet3,
                )
            )

            SectionHeading(strings.vergelijkingSectionFaq)
            FaqRow(
                question = strings.vergelijkingFaq1Question,
                answer = strings.vergelijkingFaq1Answer,
            )
            FaqRow(
                question = strings.vergelijkingFaq2Question,
                answer = strings.vergelijkingFaq2Answer,
            )
            FaqRow(
                question = strings.vergelijkingFaq3Question,
                answer = strings.vergelijkingFaq3Answer,
            )
            FaqRow(
                question = strings.vergelijkingFaq4Question,
                answer = strings.vergelijkingFaq4Answer,
            )
        }
    }
}

@Composable
private fun SectionHeading(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .fontSize(26.px)
            .fontWeight(FontWeight.SemiBold)
            .color(SiteColors.textLight)
            .padding(top = 12.px),
    )
}

@Composable
private fun Paragraph(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .fontSize(16.px)
            .lineHeight(1.65)
            .color(SiteColors.textMutedLight),
    )
}

@Composable
private fun BulletList(items: List<String>) {
    Ul(attrs = {
        style {
            property("padding-left", "20px")
            property("margin", "0")
        }
    }) {
        items.forEach { item ->
            Li(attrs = {
                style {
                    fontSize(16.px)
                    property("line-height", "1.7")
                    color(rgb(0x55, 0x55, 0x55))
                    property("margin-bottom", "8px")
                }
            }) {
                Text(item)
            }
        }
    }
}

@Composable
private fun FaqRow(question: String, answer: String) {
    Column(verticalArrangement = Arrangement.spacedBy(6.px)) {
        SpanText(
            question,
            modifier = Modifier
                .fontSize(17.px)
                .fontWeight(FontWeight.SemiBold)
                .color(SiteColors.textLight),
        )
        SpanText(
            answer,
            modifier = Modifier
                .fontSize(15.px)
                .lineHeight(1.65)
                .color(SiteColors.textMutedLight),
        )
    }
}

@Composable
private fun ComparisonTable(strings: SiteStrings) {
    Div(attrs = {
        style {
            property("overflow-x", "auto")
            property("width", "100%")
        }
    }) {
        Table(attrs = {
            style {
                property("width", "100%")
                property("border-collapse", "collapse")
                property("background-color", "#FFFFFF")
                property("border-radius", "12px")
                property("overflow", "hidden")
                property("font-size", "14px")
                property("min-width", "640px")
            }
        }) {
            Thead {
                Tr(attrs = {
                    style {
                        property("background-color", "#F2F4F7")
                    }
                }) {
                    Th(attrs = { applyHeaderCellStyle(textAlignLeft = true) }) { Text(strings.vergelijkingTableFeatureHeader) }
                    Th(attrs = { applyHeaderCellStyle() }) { Text("Padelchecker") }
                    Th(attrs = { applyHeaderCellStyle() }) { Text("Playtomic") }
                    Th(attrs = { applyHeaderCellStyle() }) { Text(strings.vergelijkingTableKnltbHeader) }
                    Th(attrs = { applyHeaderCellStyle() }) { Text(strings.vergelijkingTableStandaloneHeader) }
                }
            }
            Tbody {
                with(strings) {
                    ComparisonRow(vergelijkingRowMultiPlatform, vergelijkingYes, vergelijkingNo, vergelijkingNo, vergelijkingNo)
                    ComparisonRow(vergelijkingRowAggregates, "600+", vergelijkingRowAggregatesOwnNetwork, vergelijkingRowAggregatesKnltbClubs, vergelijkingRowAggregatesOneClub)
                    ComparisonRow(vergelijkingRowRealtime, vergelijkingYes, vergelijkingYes, vergelijkingYes, vergelijkingYes)
                    ComparisonRow(vergelijkingRowPushNotifications, vergelijkingRowPushNotificationsPro, vergelijkingRowPushNotificationsLimited, vergelijkingNo, vergelijkingNo)
                    ComparisonRow(vergelijkingRowPadelPoll, vergelijkingYes, vergelijkingNo, vergelijkingNo, vergelijkingNo)
                    ComparisonRow(vergelijkingRowDirectBooking, vergelijkingRowDirectBookingRedirect, vergelijkingYes, vergelijkingYes, vergelijkingYes)
                    ComparisonRow(vergelijkingRowFree, vergelijkingYes, vergelijkingYes, vergelijkingYes, vergelijkingYes)
                }
            }
        }
    }
}

@Composable
private fun ComparisonRow(
    feature: String,
    padelchecker: String,
    playtomic: String,
    knltb: String,
    losseClub: String,
) {
    Tr(attrs = {
        style {
            property("border-top", "1px solid #E5E7EB")
        }
    }) {
        Td(attrs = { applyCellStyle(textAlignLeft = true, isFeatureName = true) }) { Text(feature) }
        Td(attrs = { applyCellStyle(highlight = true) }) { Text(padelchecker) }
        Td(attrs = { applyCellStyle() }) { Text(playtomic) }
        Td(attrs = { applyCellStyle() }) { Text(knltb) }
        Td(attrs = { applyCellStyle() }) { Text(losseClub) }
    }
}

private fun org.jetbrains.compose.web.attributes.AttrsScope<*>.applyHeaderCellStyle(
    textAlignLeft: Boolean = false,
) {
    style {
        padding(12.px, 14.px)
        fontSize(13.px)
        fontWeight(600)
        color(rgb(0x35, 0x3A, 0x47))
        property("text-align", if (textAlignLeft) "left" else "center")
        property("white-space", "nowrap")
    }
}

private fun org.jetbrains.compose.web.attributes.AttrsScope<*>.applyCellStyle(
    textAlignLeft: Boolean = false,
    isFeatureName: Boolean = false,
    highlight: Boolean = false,
) {
    style {
        padding(12.px, 14.px)
        fontSize(14.px)
        property("line-height", "1.4")
        color(rgb(0x44, 0x44, 0x44))
        property("text-align", if (textAlignLeft) "left" else "center")
        if (isFeatureName) {
            fontWeight(500)
            color(rgb(0x1E, 0x1E, 0x1E))
        }
        if (highlight) {
            property("background-color", "#E9F0FC")
            fontWeight(600)
            color(rgb(0x1A, 0x73, 0xE8))
        }
    }
}
